import { computed, effect, inject, Injectable, Signal, signal } from '@angular/core';
import { StorageService } from '../../services/storage.service';
import { ThemeService } from '../../services/theme.service';
import { User } from '../../models/user.model';

export interface Show {
    id: number;
    name: string;
    useLegacyShowreport?: boolean;
    imageUrl?: string;
    primaryColor?: string;
    /** One of Status enum values: CONCEPT, PUBLISHED, DELETED, CANCELLED, ENDED. */
    status?: string;
    /** When true, the show uses the Toneelmeester Logboek feature. */
    hasLog?: boolean;
    /** Comma- or newline-separated weekly-report recipients; only meaningful when hasLog=true. */
    weeklyLogReportRecipients?: string;
    /** When true, the show uses the daily Performer Fly inspection feature. */
    hasPerformerFlyInspections?: boolean;
    /** When true, the show uses the rehearsal feature (rehearsal-url section on performance cards). */
    hasRehearsals?: boolean;

    // Per-show menu visibility flags. See app.component.ts requiresShowFlag.
    hasTasks?: boolean;
    hasMaintenance?: boolean;
    hasPerformances?: boolean;
    hasPlanner?: boolean;
    hasShowreports?: boolean;
    /** Default recipient address for the showreport email — see Show model. */
    showreportEmail?: string;
    hasStartupProcedure?: boolean;
    hasShowTimer?: boolean;
    hasProtocols?: boolean;
    hasShowStopProcedure?: boolean;
    hasEvacuationInstructions?: boolean;
    hasSceneryChangeList?: boolean;
    hasCallsheetButton?: boolean;
    hasEftelingShowreportButton?: boolean;
    hasCalendarFeed?: boolean;
    hasContactList?: boolean;
}

@Injectable({providedIn: 'root'})
export class AuthStateService {

    // ============ Private Writable Signals ============
    private readonly storageService = inject(StorageService);
    private readonly themeService = inject(ThemeService);
    private readonly _user = signal<User | null>(null);
    private readonly _activeShow = signal<Show | null>(null);
    private readonly _availableShows = signal<Show[]>([]);
    private readonly _token = signal<string | null>(null);
    private readonly _isInitialized = signal<boolean>(false);

    /**
     * Reactive flag — true when the signed-in user is linked to more than one
     * show. Drives the side-menu show-switcher visibility. Recomputes
     * automatically whenever {@link setAvailableShows} is called, so a refresh
     * triggered by app focus or by opening the switcher modal updates the
     * UI without any manual change-detection plumbing.
     */
    public readonly hasMultipleShows: Signal<boolean> = computed(() => this._availableShows().length > 1);

    // ============ Public Read-Only Signals (via getters) ============
    public get user() {
        return this._user.asReadonly();
    }

    public get activeShow() {
        return this._activeShow.asReadonly();
    }

    public get availableShows() {
        return this._availableShows.asReadonly();
    }

    public get token() {
        return this._token.asReadonly();
    }

    public get isInitialized() {
        return this._isInitialized.asReadonly();
    }

    // ============ Computed Values (via getters) ============
    public get isAuthenticated() {
        return this._user() !== null && this._token() !== null;
    }

    public get activeShowId() {
        return this._activeShow()?.id ?? null;
    }

    public get activeShowName() {
        return this._activeShow()?.name ?? null;
    }

    public get activeShowImageUrl() {
        return this._activeShow()?.imageUrl ?? null;
    }

    public get activeShowPrimaryColor() {
        return this._activeShow()?.primaryColor ?? null;
    }

    public get activeShowHasLog(): boolean {
        return this._activeShow()?.hasLog === true;
    }

    public get activeShowHasPerformerFlyInspections(): boolean {
        return this._activeShow()?.hasPerformerFlyInspections === true;
    }

    public get userFullName() {
        const user = this._user();
        return user ? `${ user.firstName } ${ user.lastName }`.trim() : null;
    }

    public get userId() {
        return this._user()?.id ?? null;
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    constructor() {
        // Effect to persist show changes to storage
        effect(() => {
            const show = this._activeShow();
            if (show && this._isInitialized()) {
                this.storageService.setItem('showId', show.id);
                this.storageService.setItem('showName', show.name);
                this.storageService.setItem('showUseLegacy', show.useLegacyShowreport ?? false);
            }
        });

        // Effect to apply show theme colors
        effect(() => {
            const show = this._activeShow();
            this.themeService.applyShowTheme(show?.primaryColor ?? null);
        });
    }

    // ============ Actions ============
    public setUser(user: User | null): void {
        this._user.set(user);
    }

    public setToken(token: string | null): void {
        this._token.set(token);
    }

    public setActiveShow(show: Show | null): void {
        this._activeShow.set(show);
    }

    public setAvailableShows(shows: Show[]): void {
        this._availableShows.set(shows);
    }

    // ============ Initialization ============
    public async initialize(): Promise<void> {
        if (this._isInitialized()) { return; }

        // Only load token from storage for initial auth check.
        // showId is NOT loaded from storage - it must come from the backend
        // via loadCurrentUser() to ensure we always have the correct show.
        // This prevents stale showId values from being used.
        const token = await this.storageService.getItem('token');

        if (token && this._token() === null) {
            this._token.set(token as string);
        }

        this._isInitialized.set(true);
    }

    // ============ Logout ============
    public clear(): void {
        this._user.set(null);
        this._token.set(null);
        this._activeShow.set(null);
        this._availableShows.set([]);
        this.storageService.removeItem('token');
        this.storageService.removeItem('showId');
        this.storageService.removeItem('showName');
        this.themeService.resetToDefaultTheme();
    }
}
